import { useCallback, useEffect, useState } from 'react';
import intervalToDuration from 'date-fns/intervalToDuration';

function isEqual(a: Duration, b: Duration) {
  return (
    a.years === b.years &&
    a.months === b.months &&
    a.weeks === b.weeks &&
    a.days === b.days &&
    a.hours === b.hours &&
    a.minutes === b.minutes &&
    a.seconds === b.seconds
  );
}

/**
 * Creates a callback for the target date.
 * Skips initialization and updates if called conditionally.
 * @param date
 */
export function useCountdown(date?: number): Duration {
  const format = useCallback((end: number) => {
    const now = Date.now();
    // intervalToDuration breaks without this check
    if (now > end) {
      return {};
    }
    return intervalToDuration({
      start: now,
      end,
    });
  }, []);

  const [state, setState] = useState<Duration>(date ? format(date) : {});

  const callback = useCallback(() => {
    if (date) {
      const newDuration = format(date);
      // Despite running this callback multiple times
      // we only want to commit a change to the state
      // one time a second if necessary
      if (newDuration && !isEqual(newDuration, state)) {
        setState(newDuration);
      }
    }
  }, [format, date, state]);

  useEffect(() => {
    let handle: NodeJS.Timer;
    if (date) {
      handle = setInterval(callback, 50);
    }
    return () => {
      if (handle) {
        clearInterval(handle);
      }
    };
  }, [callback, date]);

  return state;
}
